import React from 'react'
import styled from 'styled-components'
import Published from 'Event/Dashboard/editor/views/Published'
import {useEditMode} from 'Event/EditModeProvider'
import {
  Agenda,
  AgendaSectionProps,
} from 'Event/template/Townhall/Dashboard/Main/AgendaSection'
import {Draggable} from 'react-beautiful-dnd'
import {AgendaItemConfig} from 'Event/template/Townhall/Dashboard/Main/AgendaSection/AgendaItemConfig'
import Configurable from 'organization/Event/Configurable'
import Grid from 'lib/ui/Grid'
import {Speaker} from 'Event/Speakers'
import {useTownhallTemplate, useTownhallUpdate} from 'Event/template/Townhall'
import {HashMap} from 'lib/list'
import AgendaEvent from 'Event/template/Townhall/Dashboard/Main/AgendaSection/AgendaItem/AgendaEvent'
import EventTimes from 'Event/template/Townhall/Dashboard/Main/AgendaSection/AgendaItem/EventTimes'
import SpeakerNames from 'Event/template/Townhall/Dashboard/Main/AgendaSection/AgendaItem/SpeakerNames'
import SpeakerImages from 'Event/template/Townhall/Dashboard/Main/AgendaSection/AgendaItem/SpeakerImageList'
import {useAttendeeVariables} from 'Event'
import {generateHashId} from 'lib/crypto/hash'
import {useAddTranslation, useTranslationLanguage} from 'Event/LanguageProvider'
import {parseVariables} from 'lib/template'
import VisibleOnMatch from 'Event/attendee-rules/VisibleOnMatch'

export default function AgendaItem(props: {
  agenda: Agenda
  id: string
  index: number
  sectionId: string
  borderColor: string
  color?: string | null
}) {
  const {agenda, id, index, sectionId, borderColor, color} = props
  const isEditMode = useEditMode()
  const duplicate = useDuplicateAgenda(sectionId)

  if (!isEditMode) {
    return (
      <VisibleOnMatch rules={agenda.rules}>
        <Published component={agenda}>
          <AgendaItemContainer aria-label="agenda" borderColor={borderColor}>
            <AgendaEvent agenda={agenda} color={color} />
            <Content agenda={agenda} />
          </AgendaItemContainer>
        </Published>
      </VisibleOnMatch>
    )
  }

  return (
    <Draggable draggableId={id} index={index}>
      {(provided) => (
        <Configurable
          aria-label="agenda item config"
          editableProps={{
            handleProps: provided.dragHandleProps,
            onCopy: () => duplicate(id),
          }}
        >
          <AgendaItemConfig agenda={agenda} id={id} sectionId={sectionId} />

          <div ref={provided.innerRef} {...provided.draggableProps}>
            <VisibleOnMatch rules={agenda.rules}>
              <Published component={agenda}>
                <AgendaItemContainer
                  aria-label="agenda"
                  borderColor={borderColor}
                >
                  <AgendaEvent agenda={agenda} color={color} />
                  <Content agenda={agenda} />
                </AgendaItemContainer>
              </Published>
            </VisibleOnMatch>
          </div>
        </Configurable>
      )}
    </Draggable>
  )
}

function useDuplicateAgenda(sectionId: string) {
  const template = useTownhallTemplate()
  const save = useTownhallUpdate()
  const addTranslation = useAddTranslation()
  const targetLanguage = useTranslationLanguage()

  return (sourceId: string) => {
    const section = template.dashboardSections[sectionId] as AgendaSectionProps
    const source = section.items[sourceId]
    if (!source) {
      return
    }

    const position = Object.keys(section.items).length + 1
    const translations =
      template.localization?.translations?.[targetLanguage] ?? {}

    const resolve = (value: string | null | undefined) => {
      const [variable] = parseVariables(value ?? '')
      return variable ? translations[variable] ?? '' : value ?? ''
    }

    return generateHashId([
      'townhall_',
      'agenda_item',
      Date.now().toString(),
      Math.random().toString(),
    ]).then((id) => {
      save({
        localization: addTranslation({
          [`dashboardSections_${sectionId}_items_${id}_text`]: resolve(
            source.text,
          ),
          [`dashboardSections_${sectionId}_items_${id}_description`]: resolve(
            source.description,
          ),
          [`dashboardSections_${sectionId}_items_${id}_link`]: resolve(
            source.link,
          ),
        }),
        dashboardSections: {
          [sectionId]: {
            items: {
              [id]: {
                ...source,
                position,
                text: `{{dashboardSections_${sectionId}_items_${id}_text}}`,
                description: `{{dashboardSections_${sectionId}_items_${id}_description}}`,
                link: `{{dashboardSections_${sectionId}_items_${id}_link}}`,
              },
            },
          },
        },
      })
    })
  }
}

function Content(props: {agenda: Agenda}) {
  const {agenda} = props

  const {speakers, featuredSpeakers} = useTownhallTemplate()

  const v = useAttendeeVariables()
  const allSpeakers: HashMap<Speaker> = {
    ...featuredSpeakers.items,
    ...speakers.items,
  }

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={5}>
        <EventTimes agenda={agenda} />
      </Grid>
      <Grid item xs={12} md={3}>
        <DescriptionText>{v(agenda.description || '')}</DescriptionText>
      </Grid>
      <Grid item xs={12} md={2}>
        <SpeakerNames speakers={allSpeakers} agenda={agenda} />
      </Grid>
      <Grid item xs={12} md={2}>
        <SpeakerImages speakers={allSpeakers} agenda={agenda} />
      </Grid>
    </Grid>
  )
}

const AgendaItemContainer = styled.div<{borderColor: string}>`
  padding: ${(props) => `${props.theme.spacing[4]} ${props.theme.spacing[5]}`};
  margin-bottom: ${(props) => props.theme.spacing[3]};
  border-bottom: 1px solid ${(props) => props.borderColor};
  margin-bottom: 0;
`

const DescriptionText = styled.div`
  font-size: 16px;
  line-height: 170%;
  letter-spacing: 0.16px;
`
